// src/ts/app/src/theme/tokens.ts
// Foundation values were copied verbatim from theme.tsx.
// SP0 (Samply redesign) expands this with the full redesign palette — values
// copied verbatim from docs/redesign/ommuse-theme.css. Existing keys are kept so
// nothing breaks; new keys are additive. The visual rollout is opt-in: the
// gradient/glow/radius tokens are consumed via `sx`, while font + base surface
// land globally through the MUI theme (see theme.tsx).

export const tokens = {
  color: {
    /** Page/component backgrounds */
    surface: {
      /** Main app background — APP_BACKGROUND_COLOR / --bg */
      base: "#0C111D",
      /** InitialSettings dark background (legacy) */
      raisedDark: "#151718",
      /** Navbar + footer background — NAVBAR_AND_FOOTER_BACKGROUND_COLOR */
      navbarFooter: "black",
      // --- redesign surface ramp (ommuse-theme.css) ---
      /** Lifted panels — --surface */
      raised: "#151718",
      /** Chips / inputs — --surface-2 */
      raised2: "#1b1e26",
      /** Hover / active — --surface-hi */
      raisedHi: "#21242c",
      /** Sidebar / footer — --black */
      black: "#000000",
    },
    text: {
      /** Body text — TEXT_COLOR (canonical --text) */
      primary: "#C2C5C9",
      /** Skip-button muted text — SKIP_BUTTON_TEXT_COLOR */
      muted: "#889096",
      /** SVG icon fill — SVG_ICON_COLOR */
      icon: "#FFFFFF",
      /** Navbar/footer text — NAVBAR_FOOTER_TEXT_COLOR */
      navbarFooter: "white",
      /** Button label text — BUTTON_TEXT_COLOR */
      button: "white",
      // --- redesign text ramp (ommuse-theme.css) ---
      /** Primary body — --text */
      body: "#C2C5C9",
      /** Headings — --white */
      heading: "#FFFFFF",
      /** Secondary — --muted */
      secondary: "#8a929a",
      /** Tertiary / placeholder — --faint */
      faint: "#5b626a",
    },
    /** Dividers / card borders — BORDER_COLOR / --border */
    border: "#26292B",
    /** Soft hairline divider — --border-soft */
    borderSoft: "#1c1f24",
    /** Brand accent / primary interactive colour — APP_COLOR / --cyan */
    accent: "#27B6CB",
    /** Hover state of accent — APP_HOVER_COLOR / --cyan-hover */
    accentHover: "#1A8899",
    /** Text on cyan fills — --cyan-ink */
    accentInk: "#04222a",
    /** Bright top of the luminous accent — --accent-1 */
    accent1: "#3DD3E6",
    /** Deep bottom of the luminous accent — --accent-2 */
    accent2: "#1B90AB",
    /** Soft accent tint (badges/fills) — --accent-soft */
    accentSoft: "rgba(39,182,203,.14)",
    /** Secondary ambient accent, used sparingly — --violet */
    violet: "#8A6CF0",
    /** Active navbar item — NAVBAR_ACTIVE_TEXT_COLOR (equals APP_COLOR) */
    navbarActive: "#27B6CB",
    /** Warning / near-limit amber — guest quota "running low", trial-warn banner */
    warn: "#E3A549",
    /** Text on solid warn fills */
    warnInk: "#2a1d07",
    /** Danger / at-limit red — guest quota full, trial-expire banner */
    danger: "#E5575C",
    /** Text on solid danger fills */
    dangerInk: "#FFFFFF",
  },
  /** Luminous gradients (opt-in via sx) */
  gradient: {
    /** Primary CTA / active-bit fill — --accent-grad */
    accent: "linear-gradient(150deg, #3DD3E6, #27B6CB 50%, #1B90AB)",
  },
  /** Elevation / glow shadows (opt-in via sx) */
  shadow: {
    /** Accent glow — --glow */
    glow: "0 8px 24px -7px rgba(39,182,203,.55), 0 2px 6px -1px rgba(39,182,203,.28)",
    /** Stronger accent glow — --glow-strong */
    glowStrong:
      "0 12px 32px -7px rgba(39,182,203,.7), 0 0 0 1px rgba(61,211,230,.25) inset",
    /** Lit-from-above hairline — --edge-hi */
    edgeHi: "inset 0 1px 0 rgba(255,255,255,.055)",
  },
  /** Spacing function: mirrors MUI's default 8 px base.
   *  Usage: tokens.space(2) === 16 */
  space: (n: number) => n * 8,
  /** Corner radii — --radius-sm / --radius */
  radius: { sm: 10, md: 12 },
  typography: {
    /** Base font family — redesign default is Sora, Argumentum kept as fallback */
    family: "Sora, Inter, system-ui, -apple-system, sans-serif",
  },
} as const;

export type Tokens = typeof tokens;
